import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ReactNode, useCallback, useMemo, useRef, useState } from "react";
import { Rnd, RndDragCallback, RndResizeCallback } from "react-rnd";
import { useReactToPrint } from "react-to-print";
import { XButton } from "share-components/mui-wrap/button/x-button";
import { ElementToPrint } from "./element";
import { PaperSize, PrintPage } from "./print-page";
import {
  PrintPreviewDialog,
  PrintPreviewDialogProps,
} from "./print-preview-dialog";
import { RowsElementProps } from "./rows-element";

export type PrintMultiplePageItem = {
  key?: string | number;
  paperPadding?: string;
  templateImage?: string;
  elements?: ElementToPrint[] | ReactNode;
  rowsElement?: RowsElementProps;
  backgroundSize?: string;
  backgroundPosition?: string;
};

export type EditorModelPathResolverArgs = {
  pageIndex: number;
  elementIndex: number;
  element: ElementToPrint;
};

type PrintMultiplePageEditorOptions = {
  enabled?: boolean;
  getModelPathForElement?: (
    args: EditorModelPathResolverArgs,
  ) => string | undefined;
};

type PrintMultiplePagePreviewProps = PrintPreviewDialogProps & {
  fontSize?: string;
  paperSize: PaperSize;
  mode?: "portrait" | "landscape";
  printIncludeTemplate?: boolean;
  pages: PrintMultiplePageItem[];
  editorOptions?: PrintMultiplePageEditorOptions;
};

type DialogCloseEvent = Parameters<
  NonNullable<PrintPreviewDialogProps["onClose"]>
>[0];

const CM_TO_PX = 96 / 2.54;
const PAGE_PANEL_WIDTH = 360;
const DEFAULT_ELEMENT_WIDTH = "3cm";
const DEFAULT_ELEMENT_HEIGHT = "0.8cm";

const isElementCollection = (
  elements?: ElementToPrint[] | ReactNode,
): elements is ElementToPrint[] => Array.isArray(elements);

const clonePages = (
  pages: PrintMultiplePageItem[],
): PrintMultiplePageItem[] => {
  return pages.map((page) => ({
    ...page,
    elements: isElementCollection(page.elements)
      ? page.elements.map((element) => ({ ...element }))
      : page.elements,
  }));
};

const parseCssLength = (value?: string) => {
  if (!value) {
    return undefined;
  }

  const trimmedValue = value.trim();

  if (trimmedValue.endsWith("cm")) {
    const numericValue = Number.parseFloat(trimmedValue.replace("cm", ""));

    return Number.isNaN(numericValue) ? undefined : numericValue * CM_TO_PX;
  }

  if (trimmedValue.endsWith("px")) {
    const numericValue = Number.parseFloat(trimmedValue.replace("px", ""));

    return Number.isNaN(numericValue) ? undefined : numericValue;
  }

  const numericValue = Number.parseFloat(trimmedValue);

  return Number.isNaN(numericValue) ? undefined : numericValue;
};

const pixelsToCentimeters = (value: number) => {
  return `${(value / CM_TO_PX).toFixed(2)}cm`;
};

const getEditorElementPosition = (element: ElementToPrint) => ({
  x: parseCssLength(element.left) ?? 0,
  y: parseCssLength(element.top) ?? 0,
});

const getEditorElementSize = (element: ElementToPrint) => ({
  width: parseCssLength(element.width) ?? parseCssLength(DEFAULT_ELEMENT_WIDTH),
  height:
    parseCssLength(element.height) ?? parseCssLength(DEFAULT_ELEMENT_HEIGHT),
});

const formatValueForCode = (value: string | ReactNode) => {
  if (typeof value === "string") {
    return JSON.stringify(value);
  }

  return '""';
};

const appendCodeLine = (
  lines: string[],
  key: string,
  value: string | number | undefined,
) => {
  if (value === undefined) {
    return;
  }

  lines.push(`    ${key}: ${JSON.stringify(String(value))},`);
};

const isModelPathValue = (value: string) => {
  const trimmedValue = value.trim();

  if (!trimmedValue || trimmedValue.includes(" ")) {
    return false;
  }

  const modelPathPattern = /^[A-Za-z_$][\w$]*(\??\.[A-Za-z_$][\w$]*)+$/;

  return modelPathPattern.test(trimmedValue);
};

const isModelPathElement = (element: ElementToPrint) => {
  if (typeof element.value === "string" && isModelPathValue(element.value)) {
    return true;
  }

  return false;
};

const getCodeValueExpression = (
  element: ElementToPrint,
  modelPath?: string,
) => {
  if (modelPath) {
    return `${modelPath} || ""`;
  }

  if (isModelPathElement(element) && typeof element.value === "string") {
    return `${element.value} || ""`;
  }

  return formatValueForCode(element.value);
};

const getPreviewValue = (element: ElementToPrint) => {
  if (isModelPathElement(element) && typeof element.value === "string") {
    return `{${element.value}}`;
  }

  return element.value;
};

const getElementCodeSnippet = (
  elements: ElementToPrint[],
  resolveModelPath?: (
    element: ElementToPrint,
    elementIndex: number,
  ) => string | undefined,
) => {
  return `const elements: ElementToPrint[] = [
${elements
  .map((element, elementIndex) => {
    const lines: string[] = [];
    const modelPath = resolveModelPath?.(element, elementIndex)?.trim();

    appendCodeLine(lines, "name", element.name);
    lines.push(`    value: ${getCodeValueExpression(element, modelPath)},`);
    appendCodeLine(lines, "top", element.top);
    appendCodeLine(lines, "left", element.left);
    appendCodeLine(lines, "fontSize", element.fontSize);
    appendCodeLine(lines, "height", element.height);
    appendCodeLine(lines, "width", element.width);
    appendCodeLine(lines, "textWrap", element.textWrap);
    appendCodeLine(lines, "fontWeight", element.fontWeight);
    appendCodeLine(lines, "textAlight", element.textAlight);
    appendCodeLine(lines, "fontStyle", element.fontStyle);
    appendCodeLine(lines, "lineHeight", element.lineHeight);
    appendCodeLine(lines, "textIndent", element.textIndent);
    appendCodeLine(lines, "lineClamp", element.lineClamp);

    return `  {
${lines.join("\n")}
  },`;
  })
  .join("\n")}
];`;
};

const buildEditorElement = (
  index: number,
  defaultFontSize?: string,
): ElementToPrint => ({
  name: `newElement${index + 1}`,
  value: "{needToDefineValue}",
  top: `${(1 + index * 0.2).toFixed(2)}cm`,
  left: `${(1 + index * 0.2).toFixed(2)}cm`,
  width: DEFAULT_ELEMENT_WIDTH,
  height: DEFAULT_ELEMENT_HEIGHT,
  fontSize: defaultFontSize,
  textWrap: "nowrap",
});

type EditorElementField = keyof ElementToPrint;

type EditorHeaderActionsProps = {
  headerAction?: ReactNode;
  isEditorAvailable?: boolean;
  isEditorMode: boolean;
  previewPages: PrintMultiplePageItem[];
  selectedPageIndex: number;
  selectedElement?: ElementToPrint;
  onPageChanged: (pageIndex: number) => void;
  onAddElement: () => void;
  onDeleteElement: () => void;
  onShowCode: () => void;
  onShowSelectedCode: () => void;
  onCopyCode: () => void;
};

type EditorModeHeaderActionsProps = {
  previewPages: PrintMultiplePageItem[];
  selectedPageIndex: number;
  selectedElement?: ElementToPrint;
  onPageChanged: (pageIndex: number) => void;
  onAddElement: () => void;
  onDeleteElement: () => void;
  onShowCode: () => void;
  onShowSelectedCode: () => void;
  onCopyCode: () => void;
};

const EditorModeHeaderActions = ({
  previewPages,
  selectedPageIndex,
  selectedElement,
  onPageChanged,
  onAddElement,
  onDeleteElement,
  onShowCode,
  onShowSelectedCode,
  onCopyCode,
}: EditorModeHeaderActionsProps) => {
  return (
    <>
      <FormControl size="small" sx={{ minWidth: "140px" }}>
        <InputLabel>Trang</InputLabel>
        <Select
          value={String(selectedPageIndex)}
          label="Trang"
          onChange={(event) => onPageChanged(Number(event.target.value))}
        >
          {previewPages.map((page, index) => (
            <MenuItem key={page.key ?? index} value={String(index)}>
              {`Trang ${index + 1}`}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <XButton variant="outlined" onClick={onAddElement}>
        Thêm element
      </XButton>
      <XButton
        variant="outlined"
        onClick={onDeleteElement}
        disabled={!selectedElement}
      >
        Xóa element
      </XButton>
      <XButton variant="outlined" onClick={onShowCode}>
        Show code
      </XButton>
      <XButton
        variant="outlined"
        onClick={onShowSelectedCode}
        disabled={!selectedElement}
      >
        Show selected code
      </XButton>
      <XButton variant="outlined" onClick={onCopyCode}>
        Copy
      </XButton>
    </>
  );
};

const EditorHeaderActions = ({
  headerAction,
  isEditorAvailable,
  isEditorMode,
  previewPages,
  selectedPageIndex,
  selectedElement,
  onPageChanged,
  onAddElement,
  onDeleteElement,
  onShowCode,
  onShowSelectedCode,
  onCopyCode,
}: EditorHeaderActionsProps) => {
  if (!isEditorAvailable) {
    return <>{headerAction}</>;
  }

  return (
    <Stack
      direction="row"
      spacing={1}
      flexWrap="wrap"
      justifyContent="flex-end"
    >
      {headerAction}
      {isEditorMode && (
        <EditorModeHeaderActions
          previewPages={previewPages}
          selectedPageIndex={selectedPageIndex}
          selectedElement={selectedElement}
          onPageChanged={onPageChanged}
          onAddElement={onAddElement}
          onDeleteElement={onDeleteElement}
          onShowCode={onShowCode}
          onShowSelectedCode={onShowSelectedCode}
          onCopyCode={onCopyCode}
        />
      )}
    </Stack>
  );
};

type EditorFooterActionsProps = {
  footerAction?: ReactNode;
  isEditorAvailable?: boolean;
  isEditorMode: boolean;
  onToggleEditorMode: () => void;
};

const EditorFooterActions = ({
  footerAction,
  isEditorAvailable,
  isEditorMode,
  onToggleEditorMode,
}: EditorFooterActionsProps) => {
  if (!isEditorAvailable) {
    return <>{footerAction}</>;
  }

  return (
    <XButton
      variant={isEditorMode ? "outlined" : "text"}
      onClick={onToggleEditorMode}
    >
      {isEditorMode ? "Preview mode" : "Editor mode"}
    </XButton>
  );
};

type CodePreviewDialogProps = {
  open: boolean;
  code: string;
  onClose: () => void;
  onCopyCode: () => void;
};

type UnsavedChangesDialogProps = {
  open: boolean;
  onClose: () => void;
  onDiscard: () => void;
};

const CodePreviewDialog = ({
  open,
  code,
  onClose,
  onCopyCode,
}: CodePreviewDialogProps) => {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Element code</DialogTitle>
      <DialogContent>
        <TextField
          label="Element code"
          multiline
          minRows={16}
          fullWidth
          value={code}
          InputProps={{ readOnly: true }}
          sx={{ marginTop: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <XButton variant="text" onClick={onClose}>
          Đóng
        </XButton>
        <XButton variant="contained" onClick={onCopyCode}>
          Copy
        </XButton>
      </DialogActions>
    </Dialog>
  );
};

const UnsavedChangesDialog = ({
  open,
  onClose,
  onDiscard,
}: UnsavedChangesDialogProps) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Xác nhận</DialogTitle>
      <DialogContent>
        <Typography>
          Bạn có thay đổi chưa lưu trong editor mode. Bạn có muốn huỷ thay đổi?
        </Typography>
      </DialogContent>
      <DialogActions>
        <XButton variant="text" onClick={onClose}>
          Tiếp tục chỉnh sửa
        </XButton>
        <XButton variant="contained" color="error" onClick={onDiscard}>
          Huỷ thay đổi
        </XButton>
      </DialogActions>
    </Dialog>
  );
};

export const PrintMultiplePagePreview = ({
  fontSize,
  paperSize,
  mode,
  pages,
  printIncludeTemplate = false,
  editorOptions,
  headerAction,
  footerAction,
  onClose,
  PaperProps,
  ...props
}: PrintMultiplePagePreviewProps) => {
  const previewDocumentRef = useRef<HTMLDivElement>(null);
  const contentDocumentRef = useRef<HTMLDivElement>(null);
  const isEditorAvailable =
    process.env.NODE_ENV === "development" && editorOptions?.enabled;
  const [isEditorMode, setIsEditorMode] = useState(false);
  const [editorPages, setEditorPages] = useState<PrintMultiplePageItem[]>(() =>
    clonePages(pages),
  );
  const [selectedPageIndex, setSelectedPageIndex] = useState(0);
  const [selectedElementIndex, setSelectedElementIndex] = useState(0);
  const [isCodeDialogOpen, setIsCodeDialogOpen] = useState(false);
  const [codeDialogContent, setCodeDialogContent] = useState("");
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [pendingAction, setPendingAction] = useState<
    "close" | "toggle-off" | null
  >(null);
  const pendingCloseRef = useRef<{
    event: DialogCloseEvent;
    reason: "backdropClick" | "escapeKeyDown";
  } | null>(null);

  const previewPages = isEditorMode ? editorPages : pages;
  const currentPage = previewPages[selectedPageIndex] ?? previewPages[0];
  const currentPageElements = useMemo<ElementToPrint[]>(
    () =>
      currentPage && isElementCollection(currentPage.elements)
        ? currentPage.elements
        : [],
    [currentPage],
  );
  const selectedElement = currentPageElements[selectedElementIndex];
  const resolveModelPathForElement = useCallback(
    (pageIndex: number, elementIndex: number, element: ElementToPrint) => {
      return editorOptions
        ?.getModelPathForElement?.({
          pageIndex,
          elementIndex,
          element,
        })
        ?.trim();
    },
    [editorOptions],
  );
  const selectedElementEditorValue = useMemo(() => {
    if (!selectedElement) {
      return "";
    }

    const modelPath = resolveModelPathForElement(
      selectedPageIndex,
      selectedElementIndex,
      selectedElement,
    );

    if (modelPath) {
      return modelPath;
    }

    return typeof selectedElement.value === "string"
      ? selectedElement.value
      : "";
  }, [
    resolveModelPathForElement,
    selectedElement,
    selectedElementIndex,
    selectedPageIndex,
  ]);
  const selectedElementIsModelPath = useMemo(() => {
    return isModelPathValue(selectedElementEditorValue);
  }, [selectedElementEditorValue]);
  const currentPageCode = useMemo(() => {
    return getElementCodeSnippet(
      currentPageElements,
      (element, elementIndex) => {
        return resolveModelPathForElement(
          selectedPageIndex,
          elementIndex,
          element,
        );
      },
    );
  }, [currentPageElements, resolveModelPathForElement, selectedPageIndex]);
  const selectedElementCode = useMemo(() => {
    if (!selectedElement) {
      return getElementCodeSnippet([]);
    }

    return getElementCodeSnippet([selectedElement], (element) => {
      return resolveModelPathForElement(
        selectedPageIndex,
        selectedElementIndex,
        element,
      );
    });
  }, [
    resolveModelPathForElement,
    selectedElement,
    selectedElementIndex,
    selectedPageIndex,
  ]);

  const handlePrintWithTemplate = useReactToPrint({
    contentRef: previewDocumentRef,
    bodyClass: "",
  });

  const handlePrintWithoutTemplate = useReactToPrint({
    contentRef: contentDocumentRef,
    bodyClass: "",
  });

  const onPrintClicked = useCallback(() => {
    if (printIncludeTemplate) {
      handlePrintWithTemplate();
      return;
    }

    handlePrintWithoutTemplate();
  }, [
    handlePrintWithTemplate,
    handlePrintWithoutTemplate,
    printIncludeTemplate,
  ]);

  const resetEditorState = useCallback(() => {
    setIsEditorMode(false);
    setEditorPages(clonePages(pages));
    setSelectedPageIndex(0);
    setSelectedElementIndex(0);
    setIsCodeDialogOpen(false);
    setHasUnsavedChanges(false);
  }, [pages]);

  const updateEditorPages = useCallback(
    (
      pageIndex: number,
      elementIndex: number,
      updater: (element: ElementToPrint) => ElementToPrint,
    ) => {
      setEditorPages((currentPages) => {
        const nextPages = clonePages(currentPages);
        const page = nextPages[pageIndex];

        if (!page || !isElementCollection(page.elements)) {
          return currentPages;
        }

        const element = page.elements[elementIndex];

        if (!element) {
          return currentPages;
        }

        page.elements[elementIndex] = updater(element);

        return nextPages;
      });
    },
    [],
  );

  const updateSelectedElementField = useCallback(
    (field: EditorElementField, value: string | ReactNode | undefined) => {
      if (!selectedElement) {
        return;
      }

      updateEditorPages(selectedPageIndex, selectedElementIndex, (element) => ({
        ...element,
        [field]: value,
      }));
      setHasUnsavedChanges(true);
    },
    [
      selectedElement,
      selectedElementIndex,
      selectedPageIndex,
      updateEditorPages,
    ],
  );

  const onToggleEditorMode = useCallback(() => {
    if (!isEditorMode) {
      setIsEditorMode(true);
      setEditorPages(clonePages(pages));
      setSelectedPageIndex(0);
      setSelectedElementIndex(0);
      setIsCodeDialogOpen(false);
      setHasUnsavedChanges(false);
      return;
    }

    if (hasUnsavedChanges) {
      setPendingAction("toggle-off");
      return;
    }

    resetEditorState();
  }, [hasUnsavedChanges, isEditorMode, pages, resetEditorState]);

  const onAddElement = useCallback(() => {
    setEditorPages((currentPages) => {
      const nextPages = clonePages(currentPages);
      const page = nextPages[selectedPageIndex];

      if (!page || !isElementCollection(page.elements)) {
        return currentPages;
      }

      page.elements.push(buildEditorElement(page.elements.length, fontSize));

      return nextPages;
    });
    setSelectedElementIndex(currentPageElements.length);
    setHasUnsavedChanges(true);
  }, [currentPageElements.length, fontSize, selectedPageIndex]);

  const onDeleteElement = useCallback(() => {
    setEditorPages((currentPages) => {
      const nextPages = clonePages(currentPages);
      const page = nextPages[selectedPageIndex];

      if (!page || !isElementCollection(page.elements)) {
        return currentPages;
      }

      page.elements.splice(selectedElementIndex, 1);

      return nextPages;
    });
    setSelectedElementIndex((currentIndex) => Math.max(currentIndex - 1, 0));
    setHasUnsavedChanges(true);
  }, [selectedElementIndex, selectedPageIndex]);

  const onCopyCode = useCallback(() => {
    if (typeof navigator === "undefined" || !navigator.clipboard) {
      return;
    }

    void navigator.clipboard.writeText(currentPageCode);
  }, [currentPageCode]);

  const onCopyDialogCode = useCallback(() => {
    if (typeof navigator === "undefined" || !navigator.clipboard) {
      return;
    }

    void navigator.clipboard.writeText(codeDialogContent);
  }, [codeDialogContent]);

  const handleClose: NonNullable<PrintPreviewDialogProps["onClose"]> =
    useCallback(
      (event, reason) => {
        if (isEditorMode && hasUnsavedChanges) {
          setPendingAction("close");
          pendingCloseRef.current = {
            event,
            reason,
          };
          return;
        }

        resetEditorState();
        onClose?.(event, reason);
      },
      [hasUnsavedChanges, isEditorMode, onClose, resetEditorState],
    );

  const closeUnsavedChangesDialog = useCallback(() => {
    setPendingAction(null);
  }, []);

  const discardPendingChanges = useCallback(() => {
    const currentPendingAction = pendingAction;
    const currentPendingClose = pendingCloseRef.current;

    setPendingAction(null);
    pendingCloseRef.current = null;
    resetEditorState();

    if (currentPendingAction === "close" && currentPendingClose) {
      onClose?.(currentPendingClose.event, currentPendingClose.reason);
    }
  }, [onClose, pendingAction, resetEditorState]);

  const onDragStop = useCallback(
    (pageIndex: number, elementIndex: number): RndDragCallback => {
      return (_event, data) => {
        updateEditorPages(pageIndex, elementIndex, (element) => ({
          ...element,
          left: pixelsToCentimeters(data.x),
          top: pixelsToCentimeters(data.y),
        }));
        setHasUnsavedChanges(true);
      };
    },
    [updateEditorPages],
  );

  const onResizeStop = useCallback(
    (pageIndex: number, elementIndex: number): RndResizeCallback => {
      return (_event, _direction, ref, _delta, position) => {
        updateEditorPages(pageIndex, elementIndex, (element) => ({
          ...element,
          left: pixelsToCentimeters(position.x),
          top: pixelsToCentimeters(position.y),
          width: pixelsToCentimeters(ref.offsetWidth),
          height: pixelsToCentimeters(ref.offsetHeight),
        }));
        setHasUnsavedChanges(true);
      };
    },
    [updateEditorPages],
  );

  const editorHeaderAction = (
    <EditorHeaderActions
      headerAction={headerAction}
      isEditorAvailable={isEditorAvailable}
      isEditorMode={isEditorMode}
      previewPages={previewPages}
      selectedPageIndex={selectedPageIndex}
      selectedElement={selectedElement}
      onPageChanged={(pageIndex) => {
        setSelectedPageIndex(pageIndex);
        setSelectedElementIndex(0);
      }}
      onAddElement={onAddElement}
      onDeleteElement={onDeleteElement}
      onShowCode={() => {
        setCodeDialogContent(currentPageCode);
        setIsCodeDialogOpen(true);
      }}
      onShowSelectedCode={() => {
        setCodeDialogContent(selectedElementCode);
        setIsCodeDialogOpen(true);
      }}
      onCopyCode={onCopyCode}
    />
  );

  const editorFooterAction = (
    <EditorFooterActions
      footerAction={footerAction}
      isEditorAvailable={isEditorAvailable}
      isEditorMode={isEditorMode}
      onToggleEditorMode={onToggleEditorMode}
    />
  );

  const renderEditorCanvas = () => {
    if (!currentPage || !isElementCollection(currentPage.elements)) {
      return (
        <Stack alignItems="center" justifyContent="center" height="100%">
          <Typography>Trang hiện tại không hỗ trợ editor mode.</Typography>
        </Stack>
      );
    }

    return (
      <Box sx={{ position: "absolute", inset: 0 }}>
        {currentPage.elements.map((element, index) => {
          const { width, height } = getEditorElementSize(element);
          const isSelectedElement = selectedElementIndex === index;
          let backgroundColor: string | undefined;

          if (isSelectedElement) {
            backgroundColor = "rgba(25, 118, 210, 0.14)";
          } else if (process.env.NODE_ENV === "development") {
            backgroundColor = "rgba(38, 237, 73, 0.2)";
          }

          const minHeight =
            process.env.NODE_ENV === "development" ? "24px" : undefined;
          const border = isSelectedElement
            ? "1px dashed #1976d2"
            : "1px dashed transparent";
          const position = getEditorElementPosition(element);
          const editorWidth = width ?? DEFAULT_ELEMENT_WIDTH;
          const editorHeight = height ?? DEFAULT_ELEMENT_HEIGHT;
          const dragStopHandler = isSelectedElement
            ? onDragStop(selectedPageIndex, index)
            : undefined;
          const resizeStopHandler = isSelectedElement
            ? onResizeStop(selectedPageIndex, index)
            : undefined;

          return (
            <Rnd
              key={`${element.name}-${index}`}
              bounds="parent"
              size={{
                width: editorWidth,
                height: editorHeight,
              }}
              position={position}
              style={{
                zIndex: isSelectedElement ? 20 : 10,
              }}
              onMouseDown={() => setSelectedElementIndex(index)}
              onDragStop={dragStopHandler}
              onResizeStop={resizeStopHandler}
              disableDragging={!isSelectedElement}
              enableResizing={isSelectedElement}
            >
              <Box
                sx={{
                  top: 0,
                  left: 0,
                  position: "absolute",
                  zIndex: "10",
                  fontSize: element.fontSize || fontSize,
                  height: "100%",
                  width: "100%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  textWrap: element.textWrap || "nowrap",
                  whiteSpace:
                    element.textWrap === "nowrap" ? "nowrap" : "normal",
                  "-webkit-line-clamp": element.lineClamp,
                  "-webkit-box-orient": "vertical",
                  backgroundColor,
                  minHeight,
                  border,
                  fontWeight: element.fontWeight,
                  textAlign: element.textAlight,
                  fontStyle: element.fontStyle,
                  lineHeight: element.lineHeight,
                  textIndent: element.textIndent,
                  cursor: isSelectedElement ? "move" : "pointer",
                }}
              >
                {getPreviewValue(element)}
              </Box>
            </Rnd>
          );
        })}
      </Box>
    );
  };

  const editorPropertiesPanel = isEditorMode ? (
    <Stack
      spacing={2}
      sx={{
        width: `${PAGE_PANEL_WIDTH}px`,
        minWidth: `${PAGE_PANEL_WIDTH}px`,
        maxWidth: `${PAGE_PANEL_WIDTH}px`,
        borderLeft: "1px solid rgba(0, 0, 0, 0.12)",
        paddingLeft: 2,
      }}
    >
      <Typography variant="h6">Properties</Typography>
      <Divider />
      {selectedElement ? (
        <Stack spacing={1.5}>
          <TextField
            label="Name"
            size="small"
            value={selectedElement.name}
            onChange={(event) =>
              updateSelectedElementField("name", event.target.value)
            }
          />
          <TextField
            label="Value"
            size="small"
            helperText={
              selectedElementIsModelPath
                ? "Model path (e.g. workOrderPoDetail?.customerDisplay?.dealingName)"
                : "Literal text shown directly on template"
            }
            multiline
            minRows={2}
            value={selectedElementEditorValue}
            onChange={(event) =>
              updateSelectedElementField("value", event.target.value)
            }
          />
          <TextField
            label="Top"
            helperText="Use cm values"
            size="small"
            value={selectedElement.top}
            onChange={(event) =>
              updateSelectedElementField("top", event.target.value)
            }
          />
          <TextField
            label="Left"
            helperText="Use cm values"
            size="small"
            value={selectedElement.left}
            onChange={(event) =>
              updateSelectedElementField("left", event.target.value)
            }
          />
          <TextField
            label="Width"
            helperText="Use cm values"
            size="small"
            value={selectedElement.width || ""}
            onChange={(event) =>
              updateSelectedElementField("width", event.target.value)
            }
          />
          <TextField
            label="Height"
            helperText="Use cm values"
            size="small"
            value={selectedElement.height || ""}
            onChange={(event) =>
              updateSelectedElementField("height", event.target.value)
            }
          />
          <TextField
            label="Font size"
            size="small"
            value={selectedElement.fontSize || ""}
            onChange={(event) =>
              updateSelectedElementField(
                "fontSize",
                event.target.value || undefined,
              )
            }
          />
          <TextField
            label="Font weight"
            size="small"
            value={selectedElement.fontWeight || ""}
            onChange={(event) =>
              updateSelectedElementField(
                "fontWeight",
                event.target.value || undefined,
              )
            }
          />
          <TextField
            label="Font style"
            size="small"
            value={selectedElement.fontStyle || ""}
            onChange={(event) =>
              updateSelectedElementField(
                "fontStyle",
                event.target.value || undefined,
              )
            }
          />
          <TextField
            label="Line height"
            size="small"
            value={selectedElement.lineHeight || ""}
            onChange={(event) =>
              updateSelectedElementField(
                "lineHeight",
                event.target.value || undefined,
              )
            }
          />
          <TextField
            label="Text indent"
            helperText="Use cm or px values"
            size="small"
            value={selectedElement.textIndent || ""}
            onChange={(event) =>
              updateSelectedElementField(
                "textIndent",
                event.target.value || undefined,
              )
            }
          />
          <FormControl size="small">
            <InputLabel>Text wrap</InputLabel>
            <Select
              value={selectedElement.textWrap || "nowrap"}
              label="Text wrap"
              onChange={(event) =>
                updateSelectedElementField(
                  "textWrap",
                  event.target.value as ElementToPrint["textWrap"],
                )
              }
            >
              <MenuItem value="nowrap">nowrap</MenuItem>
              <MenuItem value="wrap">wrap</MenuItem>
              <MenuItem value="balance">balance</MenuItem>
              <MenuItem value="stable">stable</MenuItem>
              <MenuItem value="pretty">pretty</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel>Text align</InputLabel>
            <Select
              value={selectedElement.textAlight || "left"}
              label="Text align"
              onChange={(event) =>
                updateSelectedElementField(
                  "textAlight",
                  event.target.value as ElementToPrint["textAlight"],
                )
              }
            >
              <MenuItem value="left">left</MenuItem>
              <MenuItem value="center">center</MenuItem>
              <MenuItem value="right">right</MenuItem>
              <MenuItem value="justify">justify</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Line clamp"
            size="small"
            value={selectedElement.lineClamp || ""}
            onChange={(event) =>
              updateSelectedElementField(
                "lineClamp",
                event.target.value || undefined,
              )
            }
          />
        </Stack>
      ) : (
        <Typography>Chọn một element để chỉnh sửa.</Typography>
      )}
    </Stack>
  ) : null;

  const previewContent = isEditorMode ? (
    <Stack
      direction={{ xs: "column", lg: "row" }}
      spacing={2}
      alignItems="flex-start"
    >
      <Stack spacing={1} sx={{ flex: 1, minWidth: paperSize.width }}>
        <Typography variant="subtitle2">{`Trang ${selectedPageIndex + 1} / ${previewPages.length}`}</Typography>
        <Stack spacing={0}>
          {currentPage && (
            <PrintPage
              key={currentPage.key ?? selectedPageIndex}
              className="print-preview-page"
              strictPaperBox={true}
              fontSize={fontSize}
              paperSize={paperSize}
              paperPadding={currentPage.paperPadding}
              templateImage={currentPage.templateImage}
              elements={renderEditorCanvas()}
              rowsElement={currentPage.rowsElement}
              backgroundSize={currentPage.backgroundSize}
              backgroundPosition={currentPage.backgroundPosition}
            />
          )}
        </Stack>
      </Stack>
      {editorPropertiesPanel}
    </Stack>
  ) : (
    <Stack spacing={0} ref={previewDocumentRef}>
      {previewPages.map((page, index) => (
        <PrintPage
          key={page.key ?? index}
          className="print-preview-page"
          strictPaperBox={true}
          fontSize={fontSize}
          paperSize={paperSize}
          paperPadding={page.paperPadding}
          templateImage={page.templateImage}
          elements={page.elements}
          rowsElement={page.rowsElement}
          backgroundSize={page.backgroundSize}
          backgroundPosition={page.backgroundPosition}
        />
      ))}
    </Stack>
  );

  return (
    <PrintPreviewDialog
      {...props}
      onClose={handleClose}
      PaperProps={{
        ...PaperProps,
        style: {
          ...PaperProps?.style,
          maxWidth: "none",
        },
      }}
      headerAction={editorHeaderAction}
      footerAction={editorFooterAction}
      onPrintClicked={onPrintClicked}
    >
      <style type="text/css" media="print">
        {`
              body {
                  margin: 0;
                  font-family: "Times New Roman", Times, serif;
              }
                  header,
                  footer,
                  nav {
                    display: none !important;
                  }

                  @page {
                        size: ${paperSize?.width || 0} ${paperSize?.height || 0};
                    ${mode ? `size: ${mode};` : ""}
                        margin:0 !important;
                        }

                  .print-preview-page {
                        break-after: page;
                        page-break-after: always;
                  }

                  .print-preview-page:last-child {
                        break-after: auto;
                        page-break-after: auto;
                  }

                  .print-preview-content-document {
                        position: static !important;
                        left: auto !important;
                        top: auto !important;
                  }

                  .print-preview-editor-document {
                        position: static !important;
                        left: auto !important;
                        top: auto !important;
                  }`}
      </style>

      {previewContent}

      {isEditorMode && (
        <Box
          className="print-preview-editor-document"
          sx={{
            position: "fixed",
            left: "-10000px",
            top: 0,
            zIndex: -1,
            pointerEvents: "none",
            "@media print": {
              position: "static",
              left: "auto",
              top: "auto",
            },
          }}
        >
          <Stack spacing={0} ref={previewDocumentRef}>
            {previewPages.map((page, index) => (
              <PrintPage
                key={page.key ?? `preview-${index}`}
                className="print-preview-page"
                strictPaperBox={true}
                fontSize={fontSize}
                paperSize={paperSize}
                paperPadding={page.paperPadding}
                templateImage={page.templateImage}
                elements={page.elements}
                rowsElement={page.rowsElement}
                backgroundSize={page.backgroundSize}
                backgroundPosition={page.backgroundPosition}
                showBorder={false}
              />
            ))}
          </Stack>
        </Box>
      )}

      {!printIncludeTemplate && (
        <Box
          className="print-preview-content-document"
          sx={{
            position: "fixed",
            left: "-10000px",
            top: 0,
            zIndex: -1,
            "@media print": {
              position: "static",
              left: "auto",
              top: "auto",
            },
          }}
        >
          <Stack spacing={0} ref={contentDocumentRef}>
            {previewPages.map((page, index) => (
              <PrintPage
                key={page.key ?? `content-${index}`}
                className="print-preview-page"
                strictPaperBox={true}
                fontSize={fontSize}
                paperSize={paperSize}
                paperPadding={page.paperPadding}
                elements={page.elements}
                rowsElement={page.rowsElement}
                backgroundSize={page.backgroundSize}
                backgroundPosition={page.backgroundPosition}
                showBorder={false}
              />
            ))}
          </Stack>
        </Box>
      )}

      <CodePreviewDialog
        open={isCodeDialogOpen}
        code={codeDialogContent}
        onClose={() => setIsCodeDialogOpen(false)}
        onCopyCode={onCopyDialogCode}
      />

      <UnsavedChangesDialog
        open={pendingAction !== null}
        onClose={closeUnsavedChangesDialog}
        onDiscard={discardPendingChanges}
      />
    </PrintPreviewDialog>
  );
};
